import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { enrollmentAPI, certificateAPI } from '../lib/studentAPI';
import type { Course, CourseEnrollment, Certificate } from '../types';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Download,
  Trophy,
  Award,
  Loader2,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';

export const CertificatePage = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const { student } = useAuth();
  const navigate = useNavigate();

  const [course, setCourse] = useState<Course | null>(null);
  const [enrollment, setEnrollment] = useState<CourseEnrollment | null>(null);
  const [certificate, setCertificate] = useState<Certificate | null>(null);

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);

  const [studentName, setStudentName] = useState('');

  // =========================
  // LOAD ENROLLMENT DATA - FIXED
  // =========================
  const loadCertificateData = useCallback(async () => {
    if (!courseId || !student?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const response = await enrollmentAPI.getStudentEnrollments();

      // Safe response handling (handles both array and { data: [...] } formats)
      let enrollments: CourseEnrollment[] = [];

      if (Array.isArray(response)) {
        enrollments = response;
      } else if (response && typeof response === 'object') {
        if ('data' in response && Array.isArray((response as any).data)) {
          enrollments = (response as any).data;
        } else if ('enrollments' in response && Array.isArray((response as any).enrollments)) {
          enrollments = (response as any).enrollments;
        }
      }

      const found = enrollments.find((e) =>
        String(e.courseId ?? (e.course as any)?.id) === String(courseId)
      );

      setEnrollment(found || null);
      setCourse(found?.course ?? null);
      setStudentName(student.name?.trim() || '');

      // Do NOT access .certificate here (it doesn't exist on CourseEnrollment type)
      // We'll only set certificate after successful generation

    } catch (err: any) {
      console.error('Failed to load enrollment:', err);
      toast.error(err?.message || 'Failed to load course data');
      setEnrollment(null);
      setCourse(null);
    } finally {
      setLoading(false);
    }
  }, [courseId, student?.id, student?.name]);

  useEffect(() => {
    loadCertificateData();
  }, [loadCertificateData]);

  // =========================
// GENERATE CERTIFICATE
// =========================
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleGenerateCertificate = async () => {
    if (!courseId || generating) return;

    const cleanName = studentName.trim();

    // =========================
    // VALIDATION
    // =========================
    if (cleanName.split(/\s+/).length < 2) {
      toast.error('Please enter your full name (First and Last name)');
      return;
    }

    if (cleanName.length > 120) {
      toast.error('Name is too long (max 120 characters)');
      return;
    }

    try {
      setGenerating(true);

      // =========================
      // STEP 1: GENERATE CERTIFICATE
      // =========================
      const res = await certificateAPI.generateCertificate(
        courseId,
        cleanName
      );

      console.log('Certificate response:', res);

      // =========================
      // STEP 2: USE CLEAN RESPONSE (NO GUESSING)
      // =========================
      const certData = res.data;

      if (!certData?.id) {
        console.error('Invalid certificate response:', res);
        throw new Error('Certificate ID not returned by server');
      }

      // ✅ IMPORTANT: sync state with UI
      setCertificate(certData);

      // =========================
      // STEP 3: FETCH PDF
      // =========================
       

      const blob = await certificateAPI.downloadCertificate(certData.certificateId);
       
      if (!blob) {
        throw new Error('Failed to download certificate file');
      }

      // =========================
      // STEP 4: CREATE PREVIEW URL
      // =========================
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
      }

      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);

      // =========================
      // STEP 5: UI UPDATES
      // =========================
      setNameDialogOpen(false);

      toast.success('Certificate generated successfully!', {
        description: 'Preview is now visible below.',
      });

    } catch (err: any) {
      console.error('Generation Error:', err);
      toast.error(err?.message || 'Failed to generate certificate');
    } finally {
      setGenerating(false);
    }
  };
  // =========================
  // DOWNLOAD CERTIFICATE - Improved
  // =========================
  const handleDownloadCertificate = async () => {
    if (!certificate?.certificateId || downloading) return;

    try {
      setDownloading(true);

      // Use the existing previewUrl if available, otherwise fetch new blob
      let blob;
      if (previewUrl) {
        const response = await fetch(previewUrl);
        blob = await response.blob();
      } else {
        blob = await certificateAPI.downloadCertificate(certificate.certificateId);
      }

      // Safe Filename Construction
      const safeStudentName = (certificate.studentName || 'Student')
        .replace(/[^a-zA-Z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-');
      
      const safeCourseName = (certificate.courseName || course?.title || 'Course')
        .replace(/[^a-zA-Z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-');

      const fileName = `${safeStudentName}-${safeCourseName}-Certificate.pdf`;

      // Optimized Download Trigger
      const url = window.URL.createObjectURL(blob);
      const link = document.body.appendChild(document.createElement('a'));
      link.href = url;
      link.download = fileName;
      link.style.display = 'none'; // Ensure it's hidden
      
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 200);

      toast.success('Download started');
    } catch (err: any) {
      toast.error(err?.message || 'Download failed. Please try again.');
      console.error('Download Error:', err);
    } finally {
      setDownloading(false);
    }
  };
  const handleRegenerate = () => setNameDialogOpen(true);

  // =========================
  // LOADING / NOT ENROLLED STATES
  // =========================
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!enrollment) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50">
        <div className="text-center max-w-md">
          <AlertCircle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
          <h2 className="text-2xl font-bold mb-2">Not Enrolled</h2>
          <p className="text-gray-600 mb-6">
            You need to enroll in this course to access the certificate.
          </p>
          <Button onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const progress = Number(enrollment.progress || 0);
  const isEligible = progress >= 100;

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* HEADER */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="font-medium text-gray-700 line-clamp-1">{course?.title}</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 pt-10">
        <AnimatePresence mode="wait">
          {!isEligible ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white p-12 rounded-2xl shadow-sm text-center"
            >
              <AlertCircle className="h-16 w-16 mx-auto text-amber-500 mb-6" />
              <h2 className="text-3xl font-bold mb-3">Complete the Course First</h2>
              <p className="text-gray-600 mb-8 text-lg">
                Current Progress: <span className="font-semibold">{progress}%</span>
              </p>
              <Button size="lg" onClick={() => navigate(`/learn/${courseId}`)}>
                Continue Learning
              </Button>
            </motion.div>
          ) : certificate ? (
             
          // CERTIFICATE READY (WITH PREVIEW)
          // =========================
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            animate={{ opacity: 1, scale: 1 }} 
            className="space-y-8"
          >
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                <Trophy className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Congratulations!</h1>
              <p className="text-xl text-gray-600">Your certificate is ready</p>
            </div>

            {/* PDF PREVIEW BOX */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden relative">
              {/* Subtle Header for the Previewer */}
              <div className="bg-gray-50 border-b px-4 py-2 flex items-center justify-between text-xs font-medium text-gray-500">
                <span>CERTIFICATE_PREVIEW.PDF</span>
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                  <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                  <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                </div>
              </div>

              <div className="aspect-[1.414/1] w-full bg-gray-100 flex items-center justify-center relative">
                {previewUrl ? (
                  <iframe
                    src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                    className="w-full h-full border-none"
                    title="Certificate Preview"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    <p className="text-sm text-gray-500">Loading document preview...</p>
                  </div>
                )}
                
                {/* Overlay to prevent accidental clicks inside iframe while scrolling */}
                <div className="absolute inset-0 pointer-events-none border-[12px] border-transparent"></div>
              </div>
            </div>

            {/* ACTION BUTTONS */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button 
                size="lg" 
                onClick={handleDownloadCertificate} 
                disabled={downloading || !previewUrl} 
                className="min-w-[220px] shadow-lg shadow-blue-200"
              >
                {downloading ? (
                  <Loader2 className="animate-spin h-5 w-5 mr-2" />
                ) : (
                  <Download className="h-5 w-5 mr-2" />
                )}
                Download Official PDF
              </Button>
              
              <Button 
                size="lg" 
                variant="outline" 
                onClick={handleRegenerate}
                className="border-gray-300"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Edit Name
              </Button>
            </div>

            <p className="text-center text-sm text-gray-500 italic">
              * This is an official document. Please ensure your name is spelled correctly.
            </p>
          </motion.div>
            ) : (
            // READY TO GENERATE
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white p-12 rounded-2xl shadow-sm text-center"
            >
              <Award className="h-16 w-16 mx-auto text-blue-600 mb-6" />
              <h2 className="text-3xl font-bold mb-3">Ready for Your Certificate?</h2>
              <p className="text-gray-600 mb-10 text-lg max-w-md mx-auto">
                You have successfully completed <strong>{course?.title}</strong>
              </p>

              <Button size="lg" onClick={() => setNameDialogOpen(true)}>
                Generate My Certificate
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* NAME DIALOG */}
      <Dialog open={nameDialogOpen} onOpenChange={setNameDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Name on Certificate</DialogTitle>
            <DialogDescription>
              This name will appear exactly as printed on your official certificate.
            </DialogDescription>
          </DialogHeader>

          <div className="py-6">
            <Label htmlFor="fullName">Full Name</Label>
            <Input
              id="fullName"
              value={studentName}
              onChange={(e) => setStudentName(e.target.value)}
              placeholder="John Doe"
              className="mt-2"
              autoFocus
            />
            <p className="text-xs text-gray-500 mt-2">
              Use your official full name
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setNameDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleGenerateCertificate}
              disabled={generating || studentName.trim().length < 3}
            >
              {generating ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4 mr-2" />
                  Generating...
                </>
              ) : (
                'Generate Certificate'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};